import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn, formatNumber, formatPercent, getStatusColor, getUtilizationColor } from '@/lib/utils';

export function Positions() {
  const { data, isLoading } = useQuery({
    queryKey: ['positions'],
    queryFn: () => api.getPositions(),
  });

  const positions = data?.positions || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Position Limits</h2>
          <p className="text-sm text-muted-foreground">
            Monitor position utilization against limits
          </p>
        </div>
        <div className="flex gap-2">
          <select className="h-9 rounded-md border px-3 text-sm">
            <option value="">All Clients</option>
          </select>
          <select className="h-9 rounded-md border px-3 text-sm">
            <option value="">All Products</option>
          </select>
          <select className="h-9 rounded-md border px-3 text-sm">
            <option value="">All Status</option>
            <option value="BREACH">Breach</option>
            <option value="CRITICAL">Critical</option>
            <option value="WARNING">Warning</option>
            <option value="OK">OK</option>
          </select>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Client</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Contract</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-muted-foreground">Net Position</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Spot Month</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">Single Month</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-muted-foreground">All Months</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                    Loading positions...
                  </td>
                </tr>
              ) : positions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                    No positions found
                  </td>
                </tr>
              ) : (
                positions.map((position) => (
                  <tr key={position.id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-6 py-4 font-medium">
                      {position.client?.name || position.clientId}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium">{position.contract?.symbol}</p>
                        <p className="text-sm text-muted-foreground">{position.contract?.productCode}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right font-mono">
                      <span className={position.netQuantity >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {position.netQuantity >= 0 ? '+' : ''}{formatNumber(position.netQuantity)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <UtilizationBar value={position.spotMonthUtilization} />
                    </td>
                    <td className="px-6 py-4">
                      <UtilizationBar value={position.singleMonthUtilization} />
                    </td>
                    <td className="px-6 py-4">
                      <UtilizationBar value={position.allMonthsUtilization} />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getStatusColor(position.status))}>
                        {position.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UtilizationBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full transition-all', getUtilizationColor(value))}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
      <span className="text-sm font-mono w-14 text-right">{formatPercent(value)}</span>
    </div>
  );
}
